import { useState, useEffect } from "react";
import ReactDOM from "react-dom/client";

// ── LOGIN SCREEN ──────────────────────────────────────────────────────
function LoginScreen({ onLogin }) {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  async function handleLogin() {
    if (!username.trim() || !password.trim()) return;
    setLoading(true);
    setError("");
    try {
const res = await fetch("/api/auth", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ username, password }),
      });
      const data = await res.json();
      if (!res.ok || !data.token) {
        setError(data.message || "Invalid credentials");
        return;
      }
      const expiry = Date.now() + 8 * 60 * 60 * 1000;
      localStorage.setItem("admin_token", data.token);
      localStorage.setItem("admin_expiry", String(expiry));
      onLogin();
    } catch (e) {
      setError("Connection error. Please try again.");
    } finally {
      setLoading(false);
    }
  }

  function onKeyDown(e) {
    if (e.key === "Enter") handleLogin();
  }

  return (
    <div style={loginStyles.root}>
      <div style={loginStyles.card}>
        <h2 style={loginStyles.title}>Fathom Blue</h2>
        <p style={loginStyles.sub}>Marketing Hub</p>
        <input
          type="text"
          placeholder="Username"
          value={username}
          onChange={(e) => { setUsername(e.target.value); setError(""); }}
          onKeyDown={onKeyDown}
          style={{ ...loginStyles.input, marginBottom: 10 }}
        />
        <input
          type="password"
          placeholder="Password"
          value={password}
          onChange={(e) => { setPassword(e.target.value); setError(""); }}
          onKeyDown={onKeyDown}
          style={loginStyles.input}
        />
        {error && <p style={loginStyles.error}>{error}</p>}
        <button onClick={handleLogin} disabled={loading} style={loginStyles.btn}>
          {loading ? "Signing in…" : "Login"}
        </button>
      </div>
    </div>
  );
}

const loginStyles = {
  root: {
    display: "flex", flexDirection: "column", alignItems: "center",
    justifyContent: "center", height: "100vh",
    fontFamily: "'DM Sans', 'Segoe UI', sans-serif", backgroundColor: "#0a0f1a",
  },
  card: {
    padding: "40px", border: "1px solid #1e2d40", borderRadius: "15px",
    textAlign: "center", backgroundColor: "#111827",
    boxShadow: "0 4px 24px rgba(0,0,0,0.4)", minWidth: 320,
  },
  title: { margin: "0 0 8px", color: "#f0f6ff", fontSize: 20, fontWeight: 700 },
  sub: { color: "#64748b", marginBottom: 24, fontSize: 13, margin: "0 0 24px" },
  input: {
    padding: "12px", borderRadius: "8px", border: "1px solid #1e2d40",
    backgroundColor: "#0a0f1a", color: "#f0f6ff",
    width: "220px", fontSize: "16px", outline: "none", boxSizing: "border-box",
  },
  error: { color: "#f87171", fontSize: 12, margin: "8px 0 0" },
  btn: {
    marginTop: "20px", padding: "12px 30px", backgroundColor: "#0ea5e9",
    color: "white", border: "none", borderRadius: "8px", cursor: "pointer",
    fontWeight: "bold", fontSize: 14, width: "100%",
  },
};

const TABS = [
  { id: "social", label: "Social Posts", icon: "📣" },
  { id: "support", label: "Support Bot", icon: "🤖" },
  { id: "reviews", label: "Review Replies", icon: "⭐" },
  { id: "outreach", label: "Outreach", icon: "🤝" },
  { id: "brand", label: "Brand Config", icon: "⚙️" },
  { id: "settings", label: "Settings", icon: "🔧" },
  { id: "calendar", label: "Calendar", icon: "📅" },
];

const PLATFORMS = ["Instagram", "Facebook", "X / Twitter", "LinkedIn"];
const TONES = ["Motivational", "Educational", "Playful", "Professional"];
const LANGUAGES = [
  { id: "English", label: "🇬🇧 English" },
  { id: "French",  label: "🇫🇷 French"  },
  { id: "Spanish", label: "🇪🇸 Spanish" },
];

const PLATFORM_LIMITS = {
  "Instagram": 2200,
  "Facebook": 63206,
  "X / Twitter": 280,
  "LinkedIn": 3000,
};

function langInstruction(lang) {
  return `Write this in ${lang}. Write natively in ${lang} — do not translate from English.`;
}
const TOPICS = ["App feature highlight", "Training tip", "User success story", "New update", "Free dive safety"];

async function callClaude(systemPrompt, userPrompt, messages, brandConfig) {
  const response = await fetch("/api/claude", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      max_tokens: 1000,
      system: systemPrompt,
      messages: messages ?? [{ role: "user", content: userPrompt }],
      brandConfig,
    }),
  });
  const data = await response.json();
  if (data.error) throw new Error(data.error.message ?? data.error);
  return data.content.map((b) => b.text || "").join("\n");
}

function cleanContent(text) {
  if (!text) return text;
  return text
    .replace(/^\*\*(?:Facebook|Instagram|Twitter|X|LinkedIn|TikTok)\*\*\s*:?\s*/i, "")
    .replace(/^\*\*(?:Facebook|Instagram|Twitter|X|LinkedIn|TikTok) Post\*\*\s*:?\s*/i, "")
    .replace(/^(?:Facebook|Instagram|Twitter|X|LinkedIn|TikTok)\s*:?\s*/i, "")
    .replace(/^#+\s*(?:Facebook|Instagram|Twitter|X|LinkedIn|TikTok)\s*:?\s*/i, "")
    .trim();
}

// ── SOCIAL POST GENERATOR ──────────────────────────────────────────────
const VARIATION_LABELS = [
  "Variation 1 — Standard",
  "Variation 2 — Opens with a question",
  "Variation 3 — Leads with a fact",
];

function SocialTab() {
  const [platforms, setPlatforms] = useState(["Instagram"]);
  const [tone, setTone] = useState("Motivational");
  const [topic, setTopic] = useState(TOPICS[0]);
  const [customNote, setCustomNote] = useState("");
  const [imageUrls, setImageUrls] = useState(["", "", ""]);
  const [variations, setVariations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [varCopied, setVarCopied] = useState(null);
  const [varScheduleResults, setVarScheduleResults] = useState([]);
  const [varScheduledTimes, setVarScheduledTimes] = useState([]);
  const [brandConfig, setBrandConfig] = useState(null);
  const [settings, setSettings] = useState(null);
  const [selectedLanguage, setSelectedLanguage] = useState("English");
  const [generatedLanguage, setGeneratedLanguage] = useState("English");
  const [editedVariations, setEditedVariations] = useState([]);

  useEffect(() => {
    fetch("/api/brand").then(r => r.json()).then(setBrandConfig).catch(() => {});
    fetch("/api/settings").then(r => r.json()).then(setSettings).catch(() => {});
  }, []);

  useEffect(() => {
    setEditedVariations(variations.map(v => cleanContent(v)));
  }, [variations]);

  function togglePlatform(p) {
    setPlatforms(prev => prev.includes(p) ? prev.filter(x => x !== p) : [...prev, p]);
  }

  function updateEditedVariation(idx, value) {
    setEditedVariations(prev => { const next = [...prev]; next[idx] = value; return next; });
  }

  function quickSetTime(idx, timeStr) {
    const [h, m] = timeStr.split(":").map(Number);
    const now = new Date();
    const local = new Date(now.getFullYear(), now.getMonth(), now.getDate(), h, m, 0, 0);
    const pad = n => String(n).padStart(2, '0');
    const value = `${local.getFullYear()}-${pad(local.getMonth()+1)}-${pad(local.getDate())}T${pad(h)}:${pad(m)}`;
    setVarScheduledTimes(prev => {
      const next = [...prev]; next[idx] = value; return next;
    });
  }

  async function generate() {
    if (platforms.length === 0) return;
    setLoading(true);
    setVariations([]);
    setVarScheduleResults([]);
    setGeneratedLanguage(selectedLanguage);
    function getLocalDatetimeString(hours, minutes = 0) {
      const now = new Date();
      const local = new Date(now.getFullYear(), now.getMonth(), now.getDate(), hours, minutes, 0, 0);
      const pad = n => String(n).padStart(2, '0');
      return `${local.getFullYear()}-${pad(local.getMonth()+1)}-${pad(local.getDate())}T${pad(hours)}:${pad(minutes)}`;
    }
    const morning   = getLocalDatetimeString(9);
    const afternoon = getLocalDatetimeString(12);
    const evening   = getLocalDatetimeString(18);
    setVarScheduledTimes([morning, afternoon, evening]);

    try {
      let recentPostsContext = "";
      try {
        const postsRes = await fetch("/api/posts");
        if (postsRes.ok) {
          const posts = await postsRes.json();
          if (Array.isArray(posts) && posts.length > 0) {
            const topics = posts.slice(0, 30).map(p => p.topic || p.content?.slice(0, 60)).filter(Boolean).join(", ");
            recentPostsContext = `\nRecent posts to avoid repeating: ${topics}`;
          }
        }
      } catch (_) {}

      const platformStr = platforms.join(", ");
      const sysBase = `You are a social media expert for Fathom Blue, a breath training platform for ocean explorers covering freediving, spearfishing and surfing.
Write compelling, motivational and educational posts that resonate with the ocean explorer community.
Always include these hashtags: #freediving #spearfishing #surfing #breathwork #fathomblue #oceanexplorers. Keep posts platform-appropriate in length and style.
Do not include the platform name as a label or header at the start of the post. Start directly with the post content.${recentPostsContext}`;

      const base = `Topic: ${topic}.${customNote ? ` Extra context: ${customNote}` : ""}
Platforms: ${platformStr}. Tone: ${tone}.
Return ONLY the post text, ready to publish.`;

      if (selectedLanguage === "All 3") {
        const [en, fr, es] = await Promise.all([
          callClaude(sysBase + "\n" + langInstruction("English"), `Write a ${tone.toLowerCase()} post. ${base}`, null, brandConfig),
          callClaude(sysBase + "\n" + langInstruction("French"),  `Write a ${tone.toLowerCase()} post. ${base}`, null, brandConfig),
          callClaude(sysBase + "\n" + langInstruction("Spanish"), `Write a ${tone.toLowerCase()} post. ${base}`, null, brandConfig),
        ]);
        setVariations([en, fr, es]);
      } else {
        const langNote = selectedLanguage !== "English" ? `\n${langInstruction(selectedLanguage)}` : "";
        const sys = sysBase + langNote;
        const [v1, v2, v3] = await Promise.all([
          callClaude(sys, `Write a ${tone.toLowerCase()} post. ${base}`, null, brandConfig),
          callClaude(sys, `Write a ${tone.toLowerCase()} post that leads with an engaging question. ${base}`, null, brandConfig),
          callClaude(sys, `Write a ${tone.toLowerCase()} post that leads with a surprising fact or statistic. ${base}`, null, brandConfig),
        ]);
        setVariations([v1, v2, v3]);
      }
    } catch (e) {
      setVariations(["Error: " + e.message, "", ""]);
    }
    setLoading(false);
  }

  async function scheduleVariation(idx) {
    const updateResult = result =>
      setVarScheduleResults(prev => { const next = [...prev]; next[idx] = result; return next; });

    updateResult(null);
    try {
      const content = cleanContent(editedVariations[idx] ?? variations[idx]);
      const scheduledTime = varScheduledTimes[idx];
      const res = await fetch("/api/schedule", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          content,
          platform: platforms.join(", "),
          imageUrl: imageUrls[idx] || settings?.default_image_url || "",
          scheduledTime,
        }),
      });

      if (!res.ok) {
        const data = await res.json();
        return updateResult({ success: false, message: data.error ?? "Failed to schedule post" });
      }

      fetch("/api/posts", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          content,
          platform: platforms.join(", "),
          topic,
          tone,
          image_url: imageUrls[idx] || settings?.default_image_url || "",
          status: "scheduled",
          scheduled_at: scheduledTime,
        }),
      }).catch(() => {});

      updateResult({ success: true });
    } catch (e) {
      updateResult({ success: false, message: e.message });
    }
  }

  return (
    <div style={styles.panel}>
      <h2 style={styles.panelTitle}>Social Post Generator</h2>
      <p style={styles.panelSub}>Generate 3 variations across platforms in seconds</p>

      <div style={styles.field}>
        <label style={styles.label}>Platforms (select multiple)</label>
        <div style={styles.chips}>
          {PLATFORMS.map((p) => (
            <button key={p} onClick={() => togglePlatform(p)} style={platforms.includes(p) ? styles.chipActive : styles.chip}>
              {platforms.includes(p) ? "✓ " : ""}{p}
            </button>
          ))}
        </div>
      </div>

      <div style={styles.field}>
        <label style={styles.label}>Tone</label>
        <div style={styles.chips}>
          {TONES.map((t) => (
            <button key={t} onClick={() => setTone(t)} style={tone === t ? styles.chipActive : styles.chip}>{t}</button>
          ))}
        </div>
      </div>

      <div style={styles.field}>
        <label style={styles.label}>Language</label>
        <div style={styles.chips}>
          {LANGUAGES.map(l => (
            <button key={l.id} onClick={() => setSelectedLanguage(l.id)} style={selectedLanguage === l.id ? styles.chipActive : styles.chip}>{l.label}</button>
          ))}
          <button onClick={() => setSelectedLanguage("All 3")} style={selectedLanguage === "All 3" ? styles.chipActive : styles.chip}>🌍 All 3</button>
        </div>
      </div>

      <div style={styles.field}>
        <label style={styles.label}>Topic</label>
        <select value={topic} onChange={e => setTopic(e.target.value)} style={styles.select}>
          {TOPICS.map(t => <option key={t}>{t}</option>)}
        </select>
      </div>

      <div style={styles.field}>
        <label style={styles.label}>Extra notes (optional)</label>
        <input
          value={customNote}
          onChange={e => setCustomNote(e.target.value)}
          placeholder="e.g. mention our new CO₂ table feature..."
          style={styles.input}
        />
      </div>

      <button onClick={generate} disabled={loading || platforms.length === 0} style={styles.btn}>
        {loading ? "Generating 3 variations…" : "Generate 3 Variations"}
      </button>

      {variations.map((text, idx) => !text ? null : (
        <div key={idx} style={styles.resultBox}>
          <div style={styles.resultHeader}>
            <span style={styles.resultLabel}>{generatedLanguage === "All 3" ? ["🇬🇧 English", "🇫🇷 French", "🇪🇸 Spanish"][idx] : VARIATION_LABELS[idx]}</span>
            <button
              onClick={() => {
                navigator.clipboard.writeText(editedVariations[idx] ?? text);
                setVarCopied(idx);
                setTimeout(() => setVarCopied(v => v === idx ? null : v), 2000);
              }}
              style={styles.copyBtn}
            >
              {varCopied === idx ? "✓ Copied" : "Copy"}
            </button>
          </div>
          <textarea
            value={editedVariations[idx] ?? text}
            onChange={e => updateEditedVariation(idx, e.target.value)}
            rows={Math.max(4, (editedVariations[idx] ?? text).split("\n").length + 1)}
            style={{ ...styles.textarea, width: "100%", boxSizing: "border-box", marginTop: 8 }}
          />
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 4, flexWrap: "wrap", gap: 6 }}>
            <span style={{ fontSize: 11, color: C.muted }}>
              {(editedVariations[idx] ?? text).length} characters
            </span>
            <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
              {platforms.map(p => PLATFORM_LIMITS[p] ? (
                <span key={p} style={{ fontSize: 11, color: (editedVariations[idx] ?? text).length > PLATFORM_LIMITS[p] ? "#f87171" : C.muted }}>
                  {p}: {PLATFORM_LIMITS[p].toLocaleString()} max
                </span>
              ) : null)}
            </div>
          </div>

          <div style={styles.field}>
            <label style={{ ...styles.label, marginTop: 10 }}>Image URL for this post</label>
            <input
              value={imageUrls[idx] || ""}
              onChange={e => setImageUrls(prev => { const next = [...prev]; next[idx] = e.target.value; return next; })}
              placeholder={settings?.default_image_url || "https://example.com/your-image.jpg"}
              style={{ ...styles.input, fontSize: 12 }}
            />
          </div>

          {settings && (
            <div style={{ display: "flex", gap: 6, marginTop: 6, flexWrap: "wrap" }}>
              {[
                { label: "Morning", time: settings.post_time_morning ?? "09:00" },
                { label: "Afternoon", time: settings.post_time_afternoon ?? "12:00" },
                { label: "Evening", time: settings.post_time_evening ?? "18:00" },
              ].map(({ label, time }) => (
                <button key={label} onClick={() => quickSetTime(idx, time)} style={{ ...styles.chip, fontSize: 11, padding: "3px 10px" }}>
                  {label} ({time})
                </button>
              ))}
              {settings.timezone && (
                <span style={{ fontSize: 11, color: C.muted, alignSelf: "center" }}>{settings.timezone}</span>
              )}
            </div>
          )}
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 8, flexWrap: "wrap" }}>
            <input
              type="datetime-local"
              value={varScheduledTimes[idx] ?? ""}
              onChange={e => setVarScheduledTimes(prev => {
                const next = [...prev]; next[idx] = e.target.value; return next;
              })}
              style={{ ...styles.input, marginBottom: 0, fontSize: 12, padding: "6px 10px", colorScheme: "dark" }}
            />
            <button
              onClick={() => scheduleVariation(idx)}
              disabled={!varScheduledTimes[idx]}
              style={{ ...styles.btn, marginTop: 0, background: "transparent", border: `1px solid ${C.accent}`, color: C.accent, padding: "7px 14px" }}
            >
              Schedule Post
            </button>
          </div>

          {varScheduleResults[idx] && (
            <p style={{ fontSize: 12, margin: "6px 0 0", color: varScheduleResults[idx].success ? C.success : "#f87171" }}>
              {varScheduleResults[idx].success ? "Post scheduled successfully!" : varScheduleResults[idx].message}
            </p>
          )}
        </div>
      ))}
    </div>
  );
}

// ── SUPPORT BOT PREVIEW ────────────────────────────────────────────────
function SupportTab() {
  const [messages, setMessages] = useState([
    { role: "assistant", text: "Hi! I'm the Fathom Blue support bot. Ask me anything about the app, training plans, or freediving basics." }
  ]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [brandConfig, setBrandConfig] = useState(null);

  useEffect(() => {
    fetch("/api/brand").then(r => r.json()).then(setBrandConfig).catch(() => {});
  }, []);

  const SYS = `You are the friendly, knowledgeable support bot for Fathom Blue — a breath training platform for freedivers, spearfishers and surfers.
You help users with: training plans, CO₂ and O₂ tables, progress tracking, spearfishing technique tips, safety guidelines, and subscription questions.
Be concise, warm, and safety-conscious. If a question is about a medical condition, always recommend consulting a doctor.
Support email: hello@fathomblueco.com. App platforms: iOS and Android.`;

  async function send() {
    if (!input.trim()) return;
    const userMsg = { role: "user", text: input };
    const newMsgs = [...messages, userMsg];
    setMessages(newMsgs);
    setInput("");
    setLoading(true);

    try {
      const history = newMsgs.map(m => ({ role: m.role === "assistant" ? "assistant" : "user", content: m.text }));
      const reply = await callClaude(SYS, null, history, brandConfig);
      setMessages([...newMsgs, { role: "assistant", text: reply }]);
    } catch (e) {
      setMessages([...newMsgs, { role: "assistant", text: "Sorry, something went wrong. Please try again." }]);
    }
    setLoading(false);
  }

  return (
    <div style={styles.panel}>
      <h2 style={styles.panelTitle}>In-App Support Bot</h2>
      <p style={styles.panelSub}>Preview your Claude-powered support experience</p>

      <div style={styles.chatBox}>
        {messages.map((m, i) => (
          <div key={i} style={m.role === "assistant" ? styles.msgBot : styles.msgUser}>
            <span style={styles.msgBubble(m.role === "assistant")}>{m.text}</span>
          </div>
        ))}
        {loading && (
          <div style={styles.msgBot}>
            <span style={styles.msgBubble(true)}>
              <span style={styles.typing}>●●●</span>
            </span>
          </div>
        )}
      </div>

      <div style={styles.chatInputRow}>
        <input
          value={input}
          onChange={e => setInput(e.target.value)}
          onKeyDown={e => e.key === "Enter" && send()}
          placeholder="Ask something…"
          style={{ ...styles.input, flex: 1, marginBottom: 0 }}
        />
        <button onClick={send} disabled={loading} style={{ ...styles.btn, marginTop: 0, marginLeft: 8 }}>Send</button>
      </div>

      <div style={styles.infoBox}>
        <strong>📌 How to embed this in your app:</strong> Call the Claude API from your Supabase Edge Function with this same system prompt. Add conversation history for multi-turn support.
      </div>
    </div>
  );
}

// ── REVIEW REPLY DRAFTER ───────────────────────────────────────────────
const SENTIMENTS = ["Positive ⭐⭐⭐⭐⭐", "Mixed ⭐⭐⭐", "Negative ⭐⭐"];
function ReviewTab() {
  const [sentiment, setSentiment] = useState(SENTIMENTS[0]);
  const [review, setReview] = useState("");
  const [result, setResult] = useState("");
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);
  const [selectedLanguage, setSelectedLanguage] = useState("English");
  const [brandConfig, setBrandConfig] = useState(null);

  useEffect(() => {
    fetch("/api/brand").then(r => r.json()).then(setBrandConfig).catch(() => {});
  }, []);

  async function generate() {
    if (!review.trim()) return;
    setLoading(true);
    setResult("");
    try {
      const sys = `You are the community manager for Fathom Blue, a breath training platform for ocean explorers.
Write replies to app store reviews in the tone of a knowledgeable dive buddy — warm and direct.
For positive reviews: thank them, highlight a specific detail they mentioned, invite them to share feedback.
For mixed reviews: acknowledge concerns specifically, explain what you're working on, stay positive.
For negative reviews: empathise genuinely, take ownership if appropriate, offer a resolution path (support email: hello@fathomblueco.com).
Keep replies under 100 words. Never be defensive.${selectedLanguage !== "English" ? `\n${langInstruction(selectedLanguage)}` : ""}`;
      const prompt = `Write a reply to this ${sentiment} app store review:\n\n"${review}"`;
      const out = await callClaude(sys, prompt, null, brandConfig);
      setResult(out);
    } catch (e) {
      setResult("Error: " + e.message);
    }
    setLoading(false);
  }

  function copy() {
    navigator.clipboard.writeText(result);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }

  return (
    <div style={styles.panel}>
      <h2 style={styles.panelTitle}>Review Reply Drafter</h2>
      <p style={styles.panelSub}>Paste a review, get a polished reply instantly</p>

      <div style={styles.field}>
        <label style={styles.label}>Review Sentiment</label>
        <div style={styles.chips}>
          {SENTIMENTS.map(s => (
            <button key={s} onClick={() => setSentiment(s)} style={sentiment === s ? styles.chipActive : styles.chip}>{s}</button>
          ))}
        </div>
      </div>

      <div style={styles.field}>
        <label style={styles.label}>Language</label>
        <div style={styles.chips}>
          {LANGUAGES.map(l => (
            <button key={l.id} onClick={() => setSelectedLanguage(l.id)} style={selectedLanguage === l.id ? styles.chipActive : styles.chip}>{l.label}</button>
          ))}
        </div>
      </div>

      <div style={styles.field}>
        <label style={styles.label}>Paste the Review</label>
        <textarea
          value={review}
          onChange={e => setReview(e.target.value)}
          placeholder="e.g. Great app but the CO₂ table UI is confusing..."
          rows={4}
          style={styles.textarea}
        />
      </div>

      <button onClick={generate} disabled={loading || !review.trim()} style={styles.btn}>
        {loading ? "Drafting…" : "Draft Reply"}
      </button>

      {result && (
        <div style={styles.resultBox}>
          <div style={styles.resultHeader}>
            <span style={styles.resultLabel}>Suggested Reply</span>
            <button onClick={copy} style={styles.copyBtn}>{copied ? "✓ Copied" : "Copy"}</button>
          </div>
          <p style={styles.resultText}>{result}</p>
        </div>
      )}

      <div style={styles.infoBox}>
        <strong>📌 Workflow tip:</strong> Use <strong>AppFollow</strong> or <strong>Appbot</strong> to pull reviews automatically, then pipe them through this Claude prompt. Paste approved replies back into the store.
      </div>
    </div>
  );
}

// ── OUTREACH DRAFTER ──────────────────────────────────────────────────
const OUTREACH_TYPES = ["Dive Club", "Swim School", "Spearfishing Shop", "Freediving Instructor", "Sports Influencer"];
function OutreachTab() {
  const [type, setType] = useState(OUTREACH_TYPES[0]);
  const [bizName, setBizName] = useState("");
  const [location, setLocation] = useState("");
  const [goal, setGoal] = useState("partnership");
  const [result, setResult] = useState("");
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);
  const [selectedLanguage, setSelectedLanguage] = useState("English");
  const [brandConfig, setBrandConfig] = useState(null);

  useEffect(() => {
    fetch("/api/brand").then(r => r.json()).then(setBrandConfig).catch(() => {});
  }, []);

  async function generate() {
    setLoading(true);
    setResult("");
    try {
      const sys = `You are the partnerships manager for Fathom Blue, a breath training platform for ocean explorers on iOS and Android, targeting dive clubs, swim schools, spearfishing shops, surf schools and influencers.
Write concise, personalised outreach emails that feel human — not spammy or template-like.
Value proposition: Fathom Blue helps ocean explorers train smarter with CO₂/O₂ tables, progress tracking and technique tips for freediving, spearfishing and surfing.
For partnership emails: propose mutual benefit clearly and briefly.
For affiliate/influencer emails: mention the commission structure exists (don't invent specifics).
For advertising emails: mention the app shows location-relevant businesses to nearby users.
Keep emails under 200 words. Include a clear call to action.${
  selectedLanguage !== "English"
    ? `\n${langInstruction(selectedLanguage)}${selectedLanguage === "French" ? " Adopt a slightly more formal tone as is customary in French business communication." : " Use a Latin American Spanish business tone."}`
    : ""}`;
      const prompt = `Write a ${goal === "partnership" ? "partnership proposal" : goal === "affiliate" ? "affiliate/influencer outreach" : "advertising opportunity"} email to:
Business type: ${type}
Business name: ${bizName || "[Business Name]"}
Location: ${location || "their area"}
Goal: ${goal}`;
      const out = await callClaude(sys, prompt, null, brandConfig);
      setResult(out);
    } catch (e) {
      setResult("Error: " + e.message);
    }
    setLoading(false);
  }

  function copy() {
    navigator.clipboard.writeText(result);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }

  return (
    <div style={styles.panel}>
      <h2 style={styles.panelTitle}>Outreach Email Drafter</h2>
      <p style={styles.panelSub}>Personalised pitches to clubs, businesses & influencers</p>

      <div style={styles.field}>
        <label style={styles.label}>Business / Contact Type</label>
        <div style={styles.chips}>
          {OUTREACH_TYPES.map(t => (
            <button key={t} onClick={() => setType(t)} style={type === t ? styles.chipActive : styles.chip}>{t}</button>
          ))}
        </div>
      </div>

      <div style={styles.row}>
        <div style={{ ...styles.field, flex: 1, marginRight: 8 }}>
          <label style={styles.label}>Business Name</label>
          <input value={bizName} onChange={e => setBizName(e.target.value)} placeholder="e.g. Blue Depths Dive Club" style={styles.input} />
        </div>
        <div style={{ ...styles.field, flex: 1 }}>
          <label style={styles.label}>Location</label>
          <input value={location} onChange={e => setLocation(e.target.value)} placeholder="e.g. Auckland, NZ" style={styles.input} />
        </div>
      </div>

      <div style={styles.field}>
        <label style={styles.label}>Outreach Goal</label>
        <div style={styles.chips}>
          {[["partnership", "🤝 Partnership"], ["affiliate", "💰 Affiliate / Influencer"], ["advertising", "📍 Advertise on App"]].map(([val, label]) => (
            <button key={val} onClick={() => setGoal(val)} style={goal === val ? styles.chipActive : styles.chip}>{label}</button>
          ))}
        </div>
      </div>

      <div style={styles.field}>
        <label style={styles.label}>Language</label>
        <div style={styles.chips}>
          {LANGUAGES.map(l => (
            <button key={l.id} onClick={() => setSelectedLanguage(l.id)} style={selectedLanguage === l.id ? styles.chipActive : styles.chip}>{l.label}</button>
          ))}
        </div>
      </div>

      <button onClick={generate} disabled={loading} style={styles.btn}>
        {loading ? "Writing…" : "Draft Email"}
      </button>

      {result && (
        <div style={styles.resultBox}>
          <div style={styles.resultHeader}>
            <span style={styles.resultLabel}>Draft Email</span>
            <button onClick={copy} style={styles.copyBtn}>{copied ? "✓ Copied" : "Copy"}</button>
          </div>
          <p style={styles.resultText}>{result}</p>
        </div>
      )}

      <div style={styles.infoBox}>
        <strong>📌 Scale tip:</strong> Feed a CSV of business names + locations into this prompt via a script to generate 100s of personalised emails. Use <strong>Google Places API</strong> to find relevant businesses by category near your target regions.
      </div>
    </div>
  );
}

// ── BRAND CONFIG ──────────────────────────────────────────────────────
function BrandTab() {
  const [form, setForm] = useState({
    appName: "Fathom Blue",
    tagline: "Train Wild. Dive Free.",
    brandTone: "Motivational and educational — knowledgeable dive buddy",
    keyFeatures: "CO₂ & O₂ tables, Progress tracking & stats, Spearfishing technique tips",
    targetAudiences: "Competitive freedivers, Recreational freedivers, Spearfishers, Big wave surfers",
    hashtags: "#freediving #spearfishing #surfing #breathwork #fathomblue #oceanexplorers #freedive #apnea",
    safetyDisclaimer: "Always train with a buddy. Never freedive alone. Shallow water blackout is real.",
    avoidTopics: "competitor names, unverified medical claims, dangerous breath-hold techniques",
    languages: ["English", "French", "Spanish"],
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    fetch("/api/brand")
      .then(r => r.json())
      .then(data => { setForm(f => ({ ...f, ...data })); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  function handleChange(e) {
    const { name, value } = e.target;
    setForm(f => ({ ...f, [name]: value }));
    setSaved(false);
  }

  function handleLanguage(lang) {
    setForm(f => {
      const langs = f.languages.includes(lang)
        ? f.languages.filter(l => l !== lang)
        : [...f.languages, lang];
      return { ...f, languages: langs };
    });
    setSaved(false);
  }

  async function save() {
    setSaving(true);
    try {
      await fetch("/api/brand", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      setSaved(true);
    } catch (e) {
      // ignore
    }
    setSaving(false);
  }

  if (loading) return <div style={styles.panel}><p style={{ color: C.muted }}>Loading…</p></div>;

  return (
    <div style={styles.panel}>
      <h2 style={styles.panelTitle}>Brand Config</h2>
      <p style={styles.panelSub}>Configure your brand identity for AI-generated content</p>

      <div style={styles.field}>
        <label style={styles.label}>App Name</label>
        <input name="appName" value={form.appName} onChange={handleChange} style={styles.input} />
      </div>

      <div style={styles.field}>
        <label style={styles.label}>Tagline</label>
        <input name="tagline" value={form.tagline} onChange={handleChange} style={styles.input} />
      </div>

      <div style={styles.field}>
        <label style={styles.label}>Brand Tone</label>
        <input name="brandTone" value={form.brandTone} onChange={handleChange} placeholder="e.g. professional, warm, motivational" style={styles.input} />
      </div>

      <div style={styles.field}>
        <label style={styles.label}>Key Features (comma separated)</label>
        <textarea name="keyFeatures" value={form.keyFeatures} onChange={handleChange} rows={3} style={styles.textarea} placeholder="e.g. CO₂ tables, O₂ tables, dive logs, stats" />
      </div>

      <div style={styles.field}>
        <label style={styles.label}>Target Audiences (comma separated)</label>
        <textarea name="targetAudiences" value={form.targetAudiences} onChange={handleChange} rows={3} style={styles.textarea} placeholder="e.g. freedivers, spearfishers, breath-hold explorers" />
      </div>

      <div style={styles.field}>
        <label style={styles.label}>Hashtags (comma separated)</label>
        <textarea name="hashtags" value={form.hashtags} onChange={handleChange} rows={3} style={styles.textarea} placeholder="e.g. #freediving, #apnea, #breathwork" />
      </div>

      <div style={styles.field}>
        <label style={styles.label}>Safety Disclaimer</label>
        <textarea name="safetyDisclaimer" value={form.safetyDisclaimer} onChange={handleChange} rows={3} style={styles.textarea} placeholder="e.g. Always train with a buddy. Never freedive alone." />
      </div>

      <div style={styles.field}>
        <label style={styles.label}>Avoid Topics (comma separated)</label>
        <textarea name="avoidTopics" value={form.avoidTopics} onChange={handleChange} rows={2} style={styles.textarea} placeholder="e.g. competitor names, medical claims" />
      </div>

      <div style={styles.field}>
        <label style={styles.label}>Languages</label>
        <div style={styles.chips}>
          {["English", "French", "Spanish"].map(lang => (
            <button
              key={lang}
              onClick={() => handleLanguage(lang)}
              style={form.languages.includes(lang) ? styles.chipActive : styles.chip}
            >
              {lang}
            </button>
          ))}
        </div>
      </div>

      <button onClick={save} disabled={saving} style={styles.btn}>
        {saving ? "Saving…" : "Save"}
      </button>

      {saved && <p style={{ color: C.success, fontSize: 13, margin: 0 }}>Brand config saved.</p>}
    </div>
  );
}

// ── SETTINGS ─────────────────────────────────────────────────────────
const TIMEZONES = [
  "Pacific/Auckland",
  "Australia/Sydney",
  "Australia/Melbourne",
  "Europe/Paris",
  "Europe/Rome",
  "America/New_York",
  "America/Los_Angeles",
  "America/Chicago",
  "America/Sao_Paulo",
  "Africa/Johannesburg",
  "Asia/Bangkok",
  "Asia/Tokyo",
  "Asia/Singapore",
  "UTC",
];

function SettingsTab() {
  const [form, setForm] = useState({
    timezone: "Pacific/Auckland",
    post_time_morning: "09:00",
    post_time_afternoon: "12:00",
    post_time_evening: "18:00",
    default_image_url: "",
    post_approval: "auto",
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [saveError, setSaveError] = useState("");

  useEffect(() => {
    fetch("/api/settings")
      .then(r => r.json())
      .then(data => { setForm(f => ({ ...f, ...data })); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  function handleChange(e) {
    const { name, value } = e.target;
    setForm(f => ({ ...f, [name]: value }));
    setSaved(false);
    setSaveError("");
  }

  async function save() {
    setSaving(true);
    setSaved(false);
    setSaveError("");
    try {
      const res = await fetch("/api/settings", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      if (!res.ok) {
        const data = await res.json();
        setSaveError(data.error ?? "Failed to save settings");
      } else {
        setSaved(true);
        setTimeout(() => setSaved(false), 3000);
      }
    } catch (e) {
      setSaveError(e.message);
    }
    setSaving(false);
  }

  if (loading) return <div style={styles.panel}><p style={{ color: C.muted }}>Loading…</p></div>;

  return (
    <div style={styles.panel}>
      <h2 style={styles.panelTitle}>Settings</h2>
      <p style={styles.panelSub}>Configure defaults for scheduling and integrations</p>

      <div style={styles.sectionHeader}>Regional Settings</div>

      <div style={styles.field}>
        <label style={styles.label}>Timezone</label>
        <select name="timezone" value={form.timezone} onChange={handleChange} style={styles.select}>
          {TIMEZONES.map(tz => <option key={tz} value={tz}>{tz}</option>)}
        </select>
      </div>

      <div style={styles.sectionHeader}>Scheduling Defaults</div>

      <div style={styles.row}>
        <div style={{ ...styles.field, flex: 1 }}>
          <label style={styles.label}>Morning</label>
          <input type="time" name="post_time_morning" value={form.post_time_morning} onChange={handleChange} style={{ ...styles.input, colorScheme: "dark" }} />
        </div>
        <div style={{ ...styles.field, flex: 1 }}>
          <label style={styles.label}>Afternoon</label>
          <input type="time" name="post_time_afternoon" value={form.post_time_afternoon} onChange={handleChange} style={{ ...styles.input, colorScheme: "dark" }} />
        </div>
        <div style={{ ...styles.field, flex: 1 }}>
          <label style={styles.label}>Evening</label>
          <input type="time" name="post_time_evening" value={form.post_time_evening} onChange={handleChange} style={{ ...styles.input, colorScheme: "dark" }} />
        </div>
      </div>

      <div style={styles.field}>
        <label style={styles.label}>Default Image URL</label>
        <input
          name="default_image_url"
          value={form.default_image_url}
          onChange={handleChange}
          placeholder="https://your-default-image.com/image.jpg"
          style={styles.input}
        />
        <span style={{ fontSize: 11, color: C.muted }}>Used as fallback image for Instagram when no image is provided</span>
      </div>

      <div style={styles.sectionHeader}>Workflow</div>

      <div style={styles.field}>
        <label style={styles.label}>Post Approval</label>
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {[
            { value: "auto", label: "Auto-schedule (post immediately)" },
            { value: "draft", label: "Draft mode (review before posting)" },
          ].map(opt => (
            <label key={opt.value} style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer", fontSize: 13, color: C.text }}>
              <input
                type="radio"
                name="post_approval"
                value={opt.value}
                checked={form.post_approval === opt.value}
                onChange={handleChange}
                style={{ accentColor: C.accent }}
              />
              {opt.label}
            </label>
          ))}
        </div>
      </div>

      <button onClick={save} disabled={saving} style={styles.btn}>
        {saving ? "Saving…" : "Save Settings"}
      </button>

      {saved && <p style={{ color: C.success, fontSize: 13, margin: 0 }}>Settings saved!</p>}
      {saveError && <p style={{ color: "#f87171", fontSize: 13, margin: 0 }}>{saveError}</p>}
    </div>
  );
}

// ── CALENDAR ─────────────────────────────────────────────────────────

const STATUS_COLORS = {
  scheduled: "#0ea5e9",
  published: "#10b981",
  draft: "#64748b",
  failed: "#ef4444",
};

const PLATFORM_ICONS = {
  "Instagram": "📸",
  "Facebook": "👍",
  "X / Twitter": "🐦",
  "TikTok": "🎵",
};

function getPlatformIcon(platform) {
  if (!platform) return "🌍";
  for (const [key, icon] of Object.entries(PLATFORM_ICONS)) {
    if (platform.includes(key)) return icon;
  }
  return "🌍";
}

function isSameDay(a, b) {
  return a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate();
}

function getCalendarDays(year, month) {
  const firstDay = new Date(year, month, 1);
  const lastDay = new Date(year, month + 1, 0);
  const days = [];
  const startPad = firstDay.getDay();
  for (let i = startPad; i > 0; i--) {
    days.push({ date: new Date(year, month, 1 - i), inMonth: false });
  }
  for (let d = 1; d <= lastDay.getDate(); d++) {
    days.push({ date: new Date(year, month, d), inMonth: true });
  }
  const remaining = days.length % 7;
  if (remaining !== 0) {
    for (let i = 1; i <= 7 - remaining; i++) {
      days.push({ date: new Date(year, month + 1, i), inMonth: false });
    }
  }
  return days;
}

function getWeekDays(date) {
  const start = new Date(date);
  start.setDate(date.getDate() - date.getDay());
  return Array.from({ length: 7 }, (_, i) => {
    const d = new Date(start);
    d.setDate(start.getDate() + i);
    return d;
  });
}

function CalendarTab() {
  const today = new Date();
  const [currentDate, setCurrentDate] = useState(new Date());
  const [view, setView] = useState("month");
  const [posts, setPosts] = useState([]);
  const [selectedDay, setSelectedDay] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editingPostId, setEditingPostId] = useState(null);
  const [editingTime, setEditingTime] = useState("");

  useEffect(() => {
    fetch("/api/posts")
      .then(r => r.json())
      .then(data => { setPosts(Array.isArray(data) ? data : []); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  function getPostsForDay(date) {
    return posts.filter(p => {
      const d = new Date(p.scheduled_at || p.created_at);
      return isSameDay(d, date);
    });
  }

  function navigate(dir) {
    setCurrentDate(prev => {
      const d = new Date(prev);
      if (view === "month") d.setMonth(d.getMonth() + dir);
      else d.setDate(d.getDate() + dir * 7);
      return d;
    });
  }

  async function deletePost(id) {
    try {
      await fetch(`/api/posts?id=${id}`, { method: "DELETE" });
      setPosts(prev => prev.filter(p => p.id !== id));
    } catch (_) {}
  }

  async function reschedulePost(id) {
    try {
      await fetch(`/api/posts?id=${id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ scheduled_at: editingTime }),
      });
      setPosts(prev => prev.map(p => p.id === id ? { ...p, scheduled_at: editingTime } : p));
      setEditingPostId(null);
    } catch (_) {}
  }

  const year = currentDate.getFullYear();
  const month = currentDate.getMonth();
  const weekDays = getWeekDays(currentDate);
  const monthLabel = currentDate.toLocaleDateString("en-US", { month: "long", year: "numeric" });
  const weekLabel = `${weekDays[0].toLocaleDateString("en-US", { month: "short", day: "numeric" })} – ${weekDays[6].toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}`;
  const DAY_HEADERS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
  const selectedDayPosts = selectedDay ? getPostsForDay(selectedDay) : [];

  return (
    <div style={{ display: "flex", gap: 12, minHeight: 0 }}>

      {/* ── Main calendar ── */}
      <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: 12, minWidth: 0 }}>

        {/* Header */}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 8 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <button onClick={() => navigate(-1)} style={styles.calNavBtn}>‹</button>
            <span style={{ fontSize: 15, fontWeight: 700, color: C.text, minWidth: 160, textAlign: "center" }}>
              {view === "month" ? monthLabel : weekLabel}
            </span>
            <button onClick={() => navigate(1)} style={styles.calNavBtn}>›</button>
          </div>
          <div style={{ display: "flex", gap: 6 }}>
            <button onClick={() => setCurrentDate(new Date())} style={{ ...styles.chip, fontSize: 11, padding: "4px 10px" }}>Today</button>
            <button onClick={() => setView("month")} style={view === "month" ? styles.chipActive : styles.chip}>Month</button>
            <button onClick={() => setView("week")} style={view === "week" ? styles.chipActive : styles.chip}>Week</button>
          </div>
        </div>

        {loading ? <p style={{ color: C.muted }}>Loading posts…</p> : view === "month" ? (

          /* ── Month view ── */
          <div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 2, marginBottom: 2 }}>
              {DAY_HEADERS.map(d => (
                <div key={d} style={{ textAlign: "center", fontSize: 10, fontWeight: 700, color: C.muted, padding: "3px 0", textTransform: "uppercase", letterSpacing: "0.4px" }}>{d}</div>
              ))}
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 2 }}>
              {getCalendarDays(year, month).map(({ date, inMonth }, i) => {
                const dayPosts = getPostsForDay(date);
                const isToday = isSameDay(date, today);
                const isSelected = selectedDay && isSameDay(date, selectedDay);
                return (
                  <div
                    key={i}
                    onClick={() => setSelectedDay(date)}
                    style={{
                      minHeight: 64, background: isSelected ? C.accentDim : C.surface,
                      border: `1px solid ${isToday ? C.accent : C.border}`, borderRadius: 5,
                      padding: "3px 5px", cursor: "pointer", opacity: inMonth ? 1 : 0.3,
                    }}
                  >
                    <div style={{ fontSize: 11, fontWeight: isToday ? 700 : 400, color: isToday ? C.accent : C.text, marginBottom: 3 }}>
                      {date.getDate()}
                    </div>
                    {dayPosts.slice(0, 3).map((p, pi) => (
                      <div key={pi} style={{
                        fontSize: 9, padding: "1px 3px", borderRadius: 3, marginBottom: 2,
                        background: (STATUS_COLORS[p.status] || C.muted) + "22",
                        borderLeft: `2px solid ${STATUS_COLORS[p.status] || C.muted}`,
                        color: C.text, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
                      }}>
                        {getPlatformIcon(p.platform)} {p.content?.slice(0, 18) || "Post"}
                      </div>
                    ))}
                    {dayPosts.length > 3 && (
                      <div style={{ fontSize: 9, color: C.muted }}>+{dayPosts.length - 3} more</div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

        ) : (

          /* ── Week view ── */
          <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 5 }}>
            {weekDays.map((date, i) => {
              const dayPosts = getPostsForDay(date);
              const isToday = isSameDay(date, today);
              return (
                <div key={i} style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                  <div
                    onClick={() => setSelectedDay(date)}
                    style={{
                      textAlign: "center", padding: "6px 4px", borderRadius: 6, cursor: "pointer",
                      background: isToday ? C.accentDim : C.surface,
                      border: `1px solid ${isToday ? C.accent : C.border}`,
                    }}
                  >
                    <div style={{ fontSize: 9, color: C.muted, textTransform: "uppercase", letterSpacing: "0.4px" }}>{DAY_HEADERS[i]}</div>
                    <div style={{ fontSize: 14, fontWeight: 700, color: isToday ? C.accent : C.text }}>{date.getDate()}</div>
                  </div>
                  <div style={{ display: "flex", flexDirection: "column", gap: 3, maxHeight: 500, overflowY: "auto" }}>
                    {dayPosts.map((p, pi) => (
                      <div
                        key={pi}
                        onClick={() => setSelectedDay(date)}
                        style={{
                          background: C.surface, border: `1px solid ${STATUS_COLORS[p.status] || C.border}`,
                          borderRadius: 5, padding: "5px 6px", cursor: "pointer",
                        }}
                      >
                        <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 2 }}>
                          <span style={{ fontSize: 11 }}>{getPlatformIcon(p.platform)}</span>
                          <span style={{ fontSize: 9, fontWeight: 700, color: STATUS_COLORS[p.status] || C.muted, textTransform: "uppercase" }}>{p.status}</span>
                        </div>
                        <div style={{ fontSize: 10, color: C.text, lineHeight: 1.4, overflow: "hidden", display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical" }}>
                          {p.content?.slice(0, 50)}
                        </div>
                        {p.scheduled_at && (
                          <div style={{ fontSize: 9, color: C.muted, marginTop: 2 }}>
                            {new Date(p.scheduled_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>

        )}
      </div>

      {/* ── Side panel ── */}
      {selectedDay && (
        <div style={{
          width: 256, flexShrink: 0, background: C.surface, border: `1px solid ${C.border}`,
          borderRadius: 10, padding: 14, display: "flex", flexDirection: "column", gap: 10,
          maxHeight: 580, overflowY: "auto",
        }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <span style={{ fontSize: 12, fontWeight: 700, color: C.text }}>
              {selectedDay.toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" })}
            </span>
            <button onClick={() => setSelectedDay(null)} style={{ background: "transparent", border: "none", color: C.muted, cursor: "pointer", fontSize: 18, lineHeight: 1 }}>×</button>
          </div>

          {selectedDayPosts.length === 0 ? (
            <p style={{ color: C.muted, fontSize: 12, margin: 0 }}>No posts scheduled.</p>
          ) : selectedDayPosts.map((p, i) => (
            <div key={i} style={{ background: "#0a0f1a", border: `1px solid ${C.border}`, borderRadius: 8, padding: 10, display: "flex", flexDirection: "column", gap: 6 }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <span style={{ fontSize: 12 }}>{getPlatformIcon(p.platform)} {p.platform || "Unknown"}</span>
                <span style={{ fontSize: 9, fontWeight: 700, color: STATUS_COLORS[p.status] || C.muted, textTransform: "uppercase" }}>{p.status}</span>
              </div>
              <p style={{ fontSize: 11, color: "#cbd5e1", margin: 0, lineHeight: 1.5 }}>
                {p.content?.slice(0, 100)}{p.content?.length > 100 ? "…" : ""}
              </p>
              {p.scheduled_at && (
                <div style={{ fontSize: 10, color: C.muted }}>
                  🕐 {new Date(p.scheduled_at).toLocaleString([], { dateStyle: "short", timeStyle: "short" })}
                </div>
              )}
              {editingPostId === p.id ? (
                <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
                  <input
                    type="datetime-local"
                    value={editingTime}
                    onChange={e => setEditingTime(e.target.value)}
                    style={{ ...styles.input, fontSize: 11, padding: "4px 8px", colorScheme: "dark", marginBottom: 0 }}
                  />
                  <div style={{ display: "flex", gap: 5 }}>
                    <button onClick={() => reschedulePost(p.id)} style={{ ...styles.btn, marginTop: 0, padding: "4px 10px", fontSize: 11 }}>Save</button>
                    <button onClick={() => setEditingPostId(null)} style={{ ...styles.chip, padding: "4px 10px", fontSize: 11 }}>Cancel</button>
                  </div>
                </div>
              ) : (
                <div style={{ display: "flex", gap: 5 }}>
                  <button
                    onClick={() => { setEditingPostId(p.id); setEditingTime(p.scheduled_at?.slice(0, 16) || ""); }}
                    style={{ ...styles.chip, fontSize: 11, padding: "3px 8px" }}
                  >
                    Edit time
                  </button>
                  <button
                    onClick={() => deletePost(p.id)}
                    style={{ ...styles.chip, fontSize: 11, padding: "3px 8px", color: "#f87171", borderColor: "#3b1a1a" }}
                  >
                    Delete
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── MAIN APP ──────────────────────────────────────────────────────────
export default function App() {
  const [activeTab, setActiveTab] = useState("social");
  const [session, setSession] = useState(null);
  const [checking, setChecking] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem("admin_token");
    const expiry = localStorage.getItem("admin_expiry");
    if (token && expiry && Date.now() < Number(expiry)) {
      setSession(true);
    }
    setChecking(false);
  }, []);

  function handleLogin() {
    setSession(true);
  }

  if (checking) return null;
  if (!session) return <LoginScreen onLogin={handleLogin} />;

  return (
    <div style={styles.root}>
      <div style={styles.sidebar}>
        <div style={styles.logo}>
          <div style={styles.logoMark}>🌊</div>
          <div>
            <div style={styles.logoName}>Fathom Blue</div>
            <div style={styles.logoSub}>Marketing Hub</div>
          </div>
        </div>

        <nav style={styles.nav}>
          {TABS.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              style={activeTab === tab.id ? styles.navItemActive : styles.navItem}
            >
              <span style={styles.navIcon}>{tab.icon}</span>
              {tab.label}
            </button>
          ))}
        </nav>

        <button
          onClick={() => {
            localStorage.removeItem("admin_token");
            localStorage.removeItem("admin_expiry");
            window.location.reload();
          }}
          style={styles.logoutBtn}
        >
          Log out
        </button>

        <div style={styles.sidebarFooter}>
          <div style={styles.poweredBy}>Powered by Claude API</div>
          <div style={styles.poweredSub}>Powered by Claude AI</div>
        </div>
      </div>

      <main style={styles.main}>
        {activeTab === "social" && <SocialTab />}
        {activeTab === "support" && <SupportTab />}
        {activeTab === "reviews" && <ReviewTab />}
        {activeTab === "outreach" && <OutreachTab />}
        {activeTab === "brand" && <BrandTab />}
        {activeTab === "settings" && <SettingsTab />}
        {activeTab === "calendar" && <CalendarTab />}
      </main>
    </div>
  );
}

// ── STYLES ────────────────────────────────────────────────────────────
const C = {
  bg: "#0a0f1a",
  surface: "#111827",
  border: "#1e2d40",
  accent: "#0ea5e9",
  accentDim: "#0c4a6e",
  text: "#f0f6ff",
  muted: "#64748b",
  success: "#10b981",
};

const styles = {
  root: {
    display: "flex",
    minHeight: "100vh",
    background: C.bg,
    fontFamily: "'DM Sans', 'Segoe UI', sans-serif",
    color: C.text,
  },
  sidebar: {
    width: 220,
    minHeight: "100vh",
    background: C.surface,
    borderRight: `1px solid ${C.border}`,
    display: "flex",
    flexDirection: "column",
    padding: "24px 0",
    flexShrink: 0,
  },
  logo: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    padding: "0 20px 24px",
    borderBottom: `1px solid ${C.border}`,
    marginBottom: 16,
  },
  logoMark: { fontSize: 28 },
  logoName: { fontWeight: 700, fontSize: 15, letterSpacing: "-0.3px" },
  logoSub: { fontSize: 11, color: C.muted, marginTop: 1 },
  nav: { flex: 1, display: "flex", flexDirection: "column", gap: 4, padding: "0 12px" },
  navItem: {
    display: "flex", alignItems: "center", gap: 10,
    background: "transparent", border: "none", color: C.muted,
    padding: "10px 12px", borderRadius: 8, cursor: "pointer",
    fontSize: 13, fontWeight: 500, textAlign: "left", width: "100%",
    transition: "all 0.15s",
  },
  navItemActive: {
    display: "flex", alignItems: "center", gap: 10,
    background: C.accentDim, border: "none", color: C.accent,
    padding: "10px 12px", borderRadius: 8, cursor: "pointer",
    fontSize: 13, fontWeight: 600, textAlign: "left", width: "100%",
  },
  navIcon: { fontSize: 16 },
  logoutBtn: {
    margin: "0 12px 8px",
    padding: "8px 12px",
    background: "transparent",
    border: `1px solid #3b1a1a`,
    borderRadius: 8,
    color: "#f87171",
    fontSize: 13,
    fontWeight: 500,
    cursor: "pointer",
    textAlign: "left",
    width: "calc(100% - 24px)",
  },
  sidebarFooter: { padding: "16px 20px", borderTop: `1px solid ${C.border}` },
  sectionHeader: {
    fontSize: 11, fontWeight: 700, color: C.muted,
    textTransform: "uppercase", letterSpacing: "0.5px",
    paddingBottom: 8, borderBottom: `1px solid ${C.border}`,
    marginTop: 8,
  },
  poweredBy: { fontSize: 11, color: C.muted, fontWeight: 600 },
  poweredSub: { fontSize: 10, color: "#334155", marginTop: 2 },
  main: { flex: 1, padding: "32px 40px", maxWidth: 720, overflowY: "auto" },
  panel: { display: "flex", flexDirection: "column", gap: 16 },
  panelTitle: { margin: 0, fontSize: 22, fontWeight: 700, letterSpacing: "-0.5px" },
  panelSub: { margin: 0, fontSize: 13, color: C.muted },
  row: { display: "flex", gap: 8 },
  field: { display: "flex", flexDirection: "column", gap: 6 },
  label: { fontSize: 12, fontWeight: 600, color: C.muted, textTransform: "uppercase", letterSpacing: "0.5px" },
  chips: { display: "flex", flexWrap: "wrap", gap: 6 },
  chip: {
    background: "transparent", border: `1px solid ${C.border}`,
    color: C.muted, padding: "5px 12px", borderRadius: 20,
    cursor: "pointer", fontSize: 12, fontWeight: 500,
  },
  chipActive: {
    background: C.accentDim, border: `1px solid ${C.accent}`,
    color: C.accent, padding: "5px 12px", borderRadius: 20,
    cursor: "pointer", fontSize: 12, fontWeight: 600,
  },
  select: {
    background: C.surface, border: `1px solid ${C.border}`,
    color: C.text, padding: "8px 12px", borderRadius: 8,
    fontSize: 13, outline: "none",
  },
  input: {
    background: C.surface, border: `1px solid ${C.border}`,
    color: C.text, padding: "9px 12px", borderRadius: 8,
    fontSize: 13, outline: "none", marginBottom: 0,
    "::placeholder": { color: C.muted },
  },
  textarea: {
    background: C.surface, border: `1px solid ${C.border}`,
    color: C.text, padding: "9px 12px", borderRadius: 8,
    fontSize: 13, outline: "none", resize: "vertical", fontFamily: "inherit",
  },
  btn: {
    background: C.accent, border: "none", color: "#fff",
    padding: "10px 20px", borderRadius: 8, cursor: "pointer",
    fontSize: 13, fontWeight: 600, alignSelf: "flex-start",
    marginTop: 4,
  },
  resultBox: {
    background: "#0d1f2d", border: `1px solid ${C.accentDim}`,
    borderRadius: 10, padding: 16, marginTop: 4,
  },
  resultHeader: { display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 },
  resultLabel: { fontSize: 11, color: C.accent, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.5px" },
  copyBtn: {
    background: "transparent", border: `1px solid ${C.accentDim}`,
    color: C.accent, padding: "3px 10px", borderRadius: 6,
    cursor: "pointer", fontSize: 11, fontWeight: 600,
  },
  resultText: { margin: 0, fontSize: 13, lineHeight: 1.7, color: "#cbd5e1", whiteSpace: "pre-wrap" },
  infoBox: {
    background: "#0f1e14", border: "1px solid #1a3a22",
    borderRadius: 8, padding: "12px 14px", fontSize: 12,
    color: "#86efac", lineHeight: 1.6,
  },
  chatBox: {
    background: C.surface, border: `1px solid ${C.border}`,
    borderRadius: 10, padding: 16, minHeight: 280, maxHeight: 380,
    overflowY: "auto", display: "flex", flexDirection: "column", gap: 10,
  },
  msgBot: { display: "flex", justifyContent: "flex-start" },
  msgUser: { display: "flex", justifyContent: "flex-end" },
  msgBubble: (isBot) => ({
    background: isBot ? "#1e2d40" : C.accentDim,
    color: isBot ? C.text : "#bae6fd",
    padding: "9px 13px", borderRadius: 12,
    fontSize: 13, lineHeight: 1.6, maxWidth: "80%",
  }),
  typing: { letterSpacing: 3, fontSize: 16, color: C.muted },
  chatInputRow: { display: "flex", gap: 8, alignItems: "center" },
  calNavBtn: {
    background: "transparent", border: `1px solid ${C.border}`, color: C.text,
    borderRadius: 6, padding: "2px 10px", cursor: "pointer", fontSize: 18, lineHeight: 1.4,
  },
};

const root = ReactDOM.createRoot(document.getElementById("root"));
root.render(<App />);
